import type { CopilotAnswerBlock, CopilotSpan } from './copilot.types'

/**
 * A line the AI meant as a list item: `- x`, `* x`, `• x`, `1. x`, `2) x`.
 *
 * The space after the marker is what keeps `**Bold:** …` out: that line opens
 * with two asterisks and no space, so it reads as prose rather than as a list
 * of one.
 */
const LIST_ITEM = /^\s*(?:[-*•‣]|\d+[.)])\s+/

/** A markdown heading: one to six hashes and a space. */
const HEADING = /^\s*#{1,6}\s+/

/** A `**bold**` run. Non-greedy so two runs on one line stay two runs. */
const BOLD = /\*\*(.+?)\*\*/

/**
 * Splits one line into plain and bold runs.
 *
 * An unmatched `**` is left as text on purpose. The AI may not be writing
 * markdown at all, and turning a stray pair of asterisks into formatting is a
 * guess about what it meant.
 *
 * @example parseInlineSpans('a **b** c')
 * // [{text:'a ',bold:false},{text:'b',bold:true},{text:' c',bold:false}]
 */
export function parseInlineSpans(text: string): CopilotSpan[] {
  const spans: CopilotSpan[] = []
  let rest = text

  let match = BOLD.exec(rest)
  while (match) {
    if (match.index > 0) spans.push({ text: rest.slice(0, match.index), bold: false })
    spans.push({ text: match[1], bold: true })
    rest = rest.slice(match.index + match[0].length)
    match = BOLD.exec(rest)
  }
  if (rest) spans.push({ text: rest, bold: false })

  return spans
}

/**
 * Reads a Copilot answer as the blocks the AI wrote, rather than as one bullet
 * per line.
 *
 * This is the whole of BRAI-604. The answer used to be split on every newline
 * with a `•` put in front of each piece, so a paragraph the AI happened to wrap
 * arrived as a column of bullets and a numbered list lost its numbers. Here a
 * bullet is drawn only where the AI actually opened a list item; consecutive
 * prose lines are one paragraph, and a blank line ends it.
 *
 * Written to survive either kind of answer, since nothing in this repo pins
 * down whether the service sends markdown: plain prose comes back as
 * paragraphs, and markdown keeps its headings, lists and bold.
 *
 * @param answer The raw `answer` string off an `assist.card`.
 * @returns Blocks in the order they were written; empty for a blank answer.
 */
export function parseAnswerBlocks(answer: string): CopilotAnswerBlock[] {
  const blocks: CopilotAnswerBlock[] = []
  let paragraph: string[] = []
  let list: string[] = []

  function flushParagraph() {
    if (paragraph.length === 0) return
    // Joined with a space, not a newline: a wrapped sentence is one sentence,
    // and keeping the break is what made it read as separate items.
    blocks.push({ kind: 'paragraph', spans: parseInlineSpans(paragraph.join(' ')) })
    paragraph = []
  }

  function flushList() {
    if (list.length === 0) return
    blocks.push({ kind: 'list', items: list.map(parseInlineSpans) })
    list = []
  }

  for (const raw of answer.split('\n')) {
    const line = raw.trim()
    if (!line) {
      flushParagraph()
      flushList()
      continue
    }

    if (HEADING.test(line)) {
      flushParagraph()
      flushList()
      blocks.push({ kind: 'heading', spans: parseInlineSpans(line.replace(HEADING, '')) })
      continue
    }

    if (LIST_ITEM.test(line)) {
      flushParagraph()
      list.push(line.replace(LIST_ITEM, ''))
      continue
    }

    flushList()
    paragraph.push(line)
  }

  flushParagraph()
  flushList()
  return blocks
}
